<template>
  <div class="content-transcript format-letter bg-unofficial">
    <div class="flex mb-2">
      <div class="w-1/2">
        <h1 class="text-[23px] text-[#2d82b5] font-bold">
          {{ dataTranscript.school.name }}
        </h1>
        <div class="mt-3 text-[11px]">
          <p>
            School code:
            {{ dataTranscript.school.school_code }}
          </p>
          <p>
            Phone:
            {{ dataTranscript.school.phone }}
          </p>
          <p>
            Website:
            {{ dataTranscript.school.website }}
          </p>
          <p>
            Address:
            {{ dataTranscript.school.address }}
          </p>
        </div>
      </div>
      <div class="w-1/2 text-[11px]">
        <div class="flex">
          <div class="w-1/2">
            <p class="font-bold">
              Name:
              {{ dataTranscript.student.full_name }}
            </p>
            <p>
              Graduation date:
              {{
                convertDateFromServer(dataTranscript.student.graduation, "date")
              }}
            </p>
            <p>
              Grade:
              {{ dataTranscript.student.grade }}
            </p>
          </div>
          <div class="w-1/2 pl-2">
            <p class="font-bold">
              Student ID:
              {{ dataTranscript.student.student_code }}
            </p>
            <p>
              Enrollment date:
              {{
                convertDateFromServer(dataTranscript.student.entered, "date")
              }}
            </p>
            <p>
              Date of birth:
              {{
                convertDateFromServer(dataTranscript.student.birthday, "date")
              }}
            </p>
          </div>
        </div>
        <div>
          <p class="address">
            Address:
            {{ dataTranscript.studentAddress }}
          </p>
          <p>
            Retrieval date:
            {{ retrievalDate }}
          </p>
        </div>
      </div>
    </div>
    <div class="md-layout h-[900px] overflow-hidden leading-[16px]">
      <div class="md-layout-item md-size-33 px-0">
        <div class="flex flex-col">
          <!-- ACADEMIC SUMMARY-->
          <div class="mt-3">
            <p class="text-center font-semibold col-header pb-3 text-[11px]">
              Academic summary
            </p>
            <div class="text-[9px] px-2">
              <span class="font-semibold">
                Cumulative GPA unweighted:
              </span>
              {{ dataTranscript.academicSummary.unWeighted }}
            </div>
            <div class="text-[9px] px-2">
              <span class="font-semibold">
                Class rank:
              </span>
              {{ dataTranscript.academicSummary.rankUnWeighted }}
            </div>
            <div class="text-[9px] px-2">
              <span class="font-semibold">
                Cumulative GPA weighted:
              </span>
              {{ dataTranscript.academicSummary.weighted }}
            </div>
            <div class="text-[9px] px-2">
              <span class="font-semibold">
                Class rank:
              </span>
              {{ dataTranscript.academicSummary.rankWeighted }}
            </div>
            <div class="text-[9px] px-2">
              <span class="font-semibold">
                Cumulative GPA with bonus points:
              </span>
              {{ dataTranscript.academicSummary.bonus }}
            </div>
            <div class="text-[9px] px-2">
              <span class="font-semibold">
                Class rank:
              </span>
              {{ dataTranscript.academicSummary.rankBonus }}
            </div>
          </div>

          <!-- ATTENDANCE-->
          <div class="mt-3">
            <p class="text-center font-semibold col-header pb-3 text-[11px]">
              Attendance
            </p>
            <div class="block-score block-score-attendance font-semibold mt-1">
              <div class="term">Term</div>
              <div class="school-days">School days</div>
              <div class="member-days">Member days</div>
              <div class="absent-days">Absent days</div>
              <div class="tardy-days">Tardy</div>
            </div>
            <div
              class="block-score block-score-attendance"
              v-for="(attendance, idx) in dataTranscript.attendancesData"
              :key="'attendance' + idx"
            >
              <div class="term">{{ attendance.termName }}</div>
              <div class="school-days">{{ attendance.schoolDay }}</div>
              <div class="member-days">{{ attendance.memberDay }}</div>
              <div class="absent-days">{{ attendance.absenceDay }}</div>
              <div class="tardy-days">{{ attendance.tardyDay }}</div>
            </div>
            <div
              class="block-score block-score-attendance font-bold text-[#2d82b5]"
            >
              <div class="term">
                Total:
                {{ dataTranscript.totalAttendanceData.totalTerm }}
              </div>
              <div class="school-days">
                {{ dataTranscript.totalAttendanceData.totalSchoolDay }}
              </div>
              <div class="member-days">
                {{ dataTranscript.totalAttendanceData.totalMemberDay }}
              </div>
              <div class="absent-days">
                {{ dataTranscript.totalAttendanceData.totalAbsenceDay }}
              </div>
              <div class="tardy-days">
                {{ dataTranscript.totalAttendanceData.totalTardyDay }}
              </div>
            </div>
          </div>

          <!-- CREDIT SUMMARY -->
          <div class="mt-3">
            <div class="col-header pb-3">
              <div class="text-[10px] font-bold px-1">
                <p>Credit summary</p>
                <p>
                  Curriculum program:
                  {{ dataTranscript.programName }}
                </p>
              </div>
              <div class="block-score block-score-credit">
                <div class="name">Grad.category</div>
                <div class="earned">Earned</div>
                <div class="required">Required</div>
                <div class="remaining">Remaining</div>
              </div>
            </div>
          </div>
          <div>
            <div
              v-for="(item, index) in dataTranscript.creditSummary
                .graduationCategories"
              :key="'credit summary' + index"
            >
              <div class="block-score block-score-credit">
                <div class="name">{{ item.name }}</div>
                <div class="earned">{{ item.credit }}</div>
                <div class="required">
                  {{ item.graduation_category_credit }}
                </div>
                <div class="remaining">
                  {{ item.remaining | formatScoreNegative }}
                </div>
              </div>
            </div>
            <div
              class="block-score block-score-credit font-bold text-[#2d82b5]"
            >
              <div class="name">Total</div>
              <div class="earned">
                {{ dataTranscript.creditSummary.total.earned }}
              </div>
              <div class="required">
                {{ dataTranscript.creditSummary.total.required }}
              </div>
              <div class="remaining">
                {{
                  dataTranscript.creditSummary.total.remaining
                    | formatScoreNegative
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="md-layout-item md-size-66 pl-3 pr-0 h-full">
        <div
          class="flex flex-col flex-wrap content-start gap-x-[10px] h-full overflow-hidden"
        >
          <!-- TRANSFER SCHOOL-->
          <div
            class="content-item"
            v-for="(school, index) in dataTranscript.transferSchool"
            :key="'transfer school' + index"
          >
            <div class="mt-3">
              <div class="col-header pb-3">
                <div class="text-center font-semibold text-[11px] px-1">
                  {{ school[0].informationSchool }}
                </div>
                <div class="block-score block-score-term">
                  <div class="course">Course</div>
                  <div class="mark">Mark</div>
                  <div class="weight">Weight</div>
                  <div class="credit">Credit</div>
                </div>
              </div>
            </div>
            <div
              v-for="(item, index) in school"
              :key="'transfer school data' + index"
            >
              <div class="font-bold text-[9px] p-1">
                {{ item.semester_name }}
              </div>
              <div
                class="block-score block-score-term"
                v-for="(subject, idx) in item.subjects"
                :key="'transfer school subject' + idx"
              >
                <div class="course">
                  {{ renderCourseId(subject) }} {{ subject.subject_name }}
                </div>
                <div class="mark">{{ subject.grade_letter }}</div>
                <div class="weight">{{ subject.weight }}</div>
                <div class="credit">{{ subject.credit }}</div>
              </div>
              <div
                style="color: #2d82b5; height: 25px; font-weight: bold; font-size: 9px; text-align: center; background-color: aqua"
              >
                Earned credits:
                {{ Number(item.earned.earned_credit).toFixed(2) || "" }}
                <span class="ml-5"
                  >GPA: {{ item.earned.earned_gpa || "" }}</span
                >
              </div>
            </div>
          </div>

          <!-- CURRENT SCHOOL-->
          <div class="content-item mt-3">
            <div class="col-header pb-3">
              <div class="text-center font-semibold text-[11px] px-1">
                {{ dataTranscript.school.name }}
              </div>
              <div class="block-score block-score-term">
                <div class="course">Course</div>
                <div class="mark">Mark</div>
                <div class="weight">Weight</div>
                <div class="credit">Credit</div>
              </div>
            </div>
          </div>
          <div
            class="content-item"
            v-for="(item, index) in dataTranscript.currentSchool"
            :key="'current school' + index"
          >
            <div class="font-bold text-[10px] p-1">
              {{ item.semester_name }}
            </div>
            <div
              class="block-score block-score-term"
              v-for="(subject, idx) in item.subjects"
              :key="'current school subject' + idx"
            >
              <div class="course">
                {{ renderCourseId(subject) }} {{ subject.subject_name }}
              </div>
              <div class="mark">{{ subject.grade_letter }}</div>
              <div class="weight">{{ subject.weight }}</div>
              <div class="credit">{{ subject.credit }}</div>
            </div>
            <div
              style="color: #2d82b5; height: 25px; font-weight: bold; font-size: 9px; text-align: center;"
            >
              Earned credits:
              {{ Number(item.earned.earned_credit).toFixed(2) || "" }}
              <span class="ml-5">GPA: {{ item.earned.earned_gpa || "" }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  convertDateFromServer,
  formatDateClient
} from "@/helpers/datetime-helper";
import moment from "moment";

export default {
  name: "default-transcript-pdf",
  props: ["dataTranscript"],
  inject: ["renderCourseId"],
  data() {
    return {
      convertDateFromServer,
      retrievalDate: moment().format(formatDateClient + " hh:mm A")
    };
  },
  filters: {
    formatScoreNegative(value) {
      return Number(value) < 0 ? 0 : Number(value).toFixed(2);
    }
  }
};
</script>
